import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogOverlay,
} from '@chakra-ui/modal';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, IconButton, Tooltip } from '@chakra-ui/react';
import InputField from 'components/fields/InputField';
import Error from 'components/message/Error';
import useAppQuery from 'hooks/useAppQuery';
import usePostAxiosData from 'hooks/usePostAxiosData';
import React, { useEffect, useState, useMemo } from 'react';
import toast from 'react-hot-toast';
import { config } from 'variables/config';
import { ROLES } from 'Constaints';
import { useTranslation } from 'react-i18next';
import MuiSelect from 'components/MuiSelect';
import { BsBan, BsPencilSquare } from 'react-icons/bs';
import GeoScopeManager from 'components/GeoScopeManager';

const UpdateUserInformations = (props) => {
  const { isOpen, onClose, setIsOpen, userId, fetchData } = props;
  const { t } = useTranslation('update_user_informations');
  const [canEdit, setCanEdit] = useState(false);

  const { data: user, refetch: userRefetch } = useAppQuery(
    userId ? `${config.app.api_url}/users/${userId}` : null
  );

  /** get user's geographic scope */
  const { data: geoScope, refetch: geoScopeRefetch } = useAppQuery(
    userId ? `${config.app.api_url}/users/${userId}/geo-scope` : null
  );

  /** Form validation config */
  const schema = yup.object().shape({
    firstname: yup.string().nullable(),
    lastname: yup.string().nullable(),
    phone: yup.string().nullable(),
    email: yup.string().nullable(),
  });

  const {
    register,
    handleSubmit,
    formState: {},
    setValue,
  } = useForm({
    resolver: yupResolver(schema),
  });

  // const onSubmitHandler = (data) => {
  //   handleUpdate(data);
  // };

  const [roles, setRoles] = useState([]);
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [selectedArrondissements, setSelectedArrondissements] = useState([]);

  // Clear dependent selections when parent is cleared
  useEffect(() => {
    if (!selectedRegions || selectedRegions?.length === 0) {
      setSelectedDepartments([]);
      setSelectedArrondissements([]);
    }
  }, [selectedRegions]);

  useEffect(() => {
    if (!selectedDepartments || selectedDepartments?.length === 0) {
      setSelectedArrondissements([]);
    }
  }, [selectedDepartments]);

  useEffect(() => {
    setValue('lastname', user?.data?.lastName);
    setValue('firstname', user?.data?.firstName);
    setValue('phone', user?.data?.phone);
    setValue('email', user?.data?.mail);
    if (user?.data?.roles) {
      setRoles(user.data.roles.map((role) => role.name));
    }
  }, [user?.data, setValue]);

  /** Fetch geographic data */
  const { data: regions } = useAppQuery(`${config.app.api_url}/regions`);
  const { data: departments } = useAppQuery(
    selectedRegions?.length > 0
      ? `${config.app.api_url}/regions/departments-by-regions?${selectedRegions.map((id) => `regionIds=${id}`).join('&')}`
      : null
  );
  const { data: arrondissements } = useAppQuery(
    selectedDepartments?.length > 0
      ? `${config.app.api_url}/departments/arrondissements-by-departments?${selectedDepartments.map((id) => `departmentIds=${id}`).join('&')}`
      : null
  );

  /** post create user */
  const [loading, postAxiosData, errorMassage, successMessage] = usePostAxiosData(
    `users/${userId}`,
    'PUT'
  );

  /** post update geographic scope */
  const [loadingGeoUpdate, postGeoScope, , successGeoUpdate] = usePostAxiosData(
    `users/${userId}/geo-scope`,
    'POST'
  );

  /** DELETE - Remove all geographic scope */
  const [loadingRemoveAll, deleteAllGeoScope, , successRemoveAll] = usePostAxiosData(
    `users/${userId}/geo-scope`,
    'DELETE'
  );

  /** DELETE - Partial removal of geographic scope */
  const [, deletePartialGeoScope, , successRemovePartial] = usePostAxiosData(
    `users/${userId}/geo-scope/partial`,
    'DELETE'
  );

  const handleUpdateUserInfo = async (dataYub) => {
    const data = {
      firstName: dataYub?.firstname,
      lastName: dataYub?.lastname,
      phone: dataYub?.phone,
      email: dataYub?.email,
      roles: roles,
    };

    await postAxiosData(data);
  };

  useEffect(() => {
    if (geoScope?.data) {
      const { regions = [], departments = [], arrondissements = [] } = geoScope.data;

      // Extract unique region IDs
      const regionIds = new Set(
        regions.length > 0
          ? regions.map((r) => r.id)
          : departments.length > 0
            ? departments.map((d) => d?.region?.id).filter(Boolean)
            : arrondissements.map((a) => a?.department?.region?.id).filter(Boolean)
      );

      // Extract unique department IDs
      const departmentIds = new Set(
        departments.length > 0
          ? departments.map((d) => d.id)
          : arrondissements.map((a) => a?.department?.id).filter(Boolean)
      );

      // Extract unique arrondissement IDs
      const arrondissementIds = new Set(arrondissements.map((a) => a.id).filter(Boolean));

      setSelectedRegions(Array.from(regionIds));
      setSelectedDepartments(Array.from(departmentIds));
      setSelectedArrondissements(Array.from(arrondissementIds));
    }
  }, [geoScope, successGeoUpdate, successRemoveAll, successRemovePartial]);

  const handleUpdateGeoScope = async (geoData) => {
    // geoData already contains scopeLevel and the appropriate IDs
    await postGeoScope(geoData);
    geoScopeRefetch(); // Refresh geographic scope after update
  };

  // Handle individual chip removal for regions
  const handleRegionChange = async (event, newValue) => {
    const newRegionIds = newValue?.map((item) => item.value || item.id) || [];

    // Always update state immediately for UI responsiveness
    setSelectedRegions(newRegionIds);

    const removedRegions = selectedRegions.filter((id) => !newRegionIds.includes(id));

    // If items were removed, call the API
    if (removedRegions.length > 0) {
      const removeData = {
        regionIds: removedRegions,
        departmentIds: null,
        arrondissementIds: null,
      };
      await deletePartialGeoScope(removeData);
    }
  };

  // Handle individual chip removal for departments
  const handleDepartmentChange = async (event, newValue) => {
    const newDeptIds = newValue?.map((item) => item.value || item.id) || [];

    // Always update state immediately for UI responsiveness
    setSelectedDepartments(newDeptIds);

    const removedDepartments = selectedDepartments.filter((id) => !newDeptIds.includes(id));

    // If items were removed, call the API
    if (removedDepartments.length > 0) {
      const removeData = {
        regionIds: null,
        departmentIds: removedDepartments,
        arrondissementIds: null,
      };
      await deletePartialGeoScope(removeData);
    }
  };

  // Handle individual chip removal for arrondissements
  const handleArrondissementChange = async (event, newValue) => {
    const newArronIds = newValue?.map((item) => item.value || item.id) || [];

    // Always update state immediately for UI responsiveness
    setSelectedArrondissements(newArronIds);

    const removedArrondissements = selectedArrondissements.filter(
      (id) => !newArronIds.includes(id)
    );

    // If items were removed, call the API
    if (removedArrondissements.length > 0) {
      const removeData = {
        regionIds: null,
        departmentIds: null,
        arrondissementIds: removedArrondissements,
      };
      await deletePartialGeoScope(removeData);
    }
  };

  const handleRemoveAllGeoScope = async () => {
    if (
      window.confirm(
        t('confirm_remove_all_geo_scope') ||
          'Are you sure you want to remove ALL geographic restrictions? User will have unrestricted access.'
      )
    ) {
      await deleteAllGeoScope({});
    }
  };

  const handleChangeValue = (event) => {
    setRoles(event.target.value);
  };

  useEffect(() => {
    if (successMessage?.status === 200 || successMessage?.status === 201) {
      toast.success(`${successMessage?.message || t('success_message')}`, {
        duration: 4000,
        className: 'bg-success text-white',
      });

      fetchData();
    }
  }, [successMessage]);

  useEffect(() => {
    if (successGeoUpdate?.status === 200 || successGeoUpdate?.status === 201) {
      toast.success(t('geo_scope_updated') || 'Geographic scope updated successfully', {
        duration: 3000,
        className: 'bg-success text-white',
      });
      geoScopeRefetch();
    }
  }, [successGeoUpdate]);

  useEffect(() => {
    if (successRemoveAll?.status === 200 || successRemoveAll?.status === 201) {
      toast.success(
        t('geo_scope_removed_all') ||
          'All geographic restrictions removed. User now has unrestricted access.',
        {
          duration: 4000,
          className: 'bg-success text-white',
        }
      );
      setSelectedRegions([]);
      setSelectedDepartments([]);
      setSelectedArrondissements([]);
      geoScopeRefetch();
      fetchData();
    }
  }, [successRemoveAll]);

  useEffect(() => {
    if (successRemovePartial?.status === 200 || successRemovePartial?.status === 201) {
      toast.success(
        t('geo_scope_removed_partial') || 'Selected geographic restrictions removed successfully',
        {
          duration: 3000,
          className: 'bg-success text-white',
        }
      );
      geoScopeRefetch();
      fetchData();
    }
  }, [successRemovePartial]);

  const handleClose = () => {
    onClose();
    setIsOpen(false);
    setCanEdit(false);
  };
  useEffect(() => {
    if (isOpen) {
      userRefetch();
    }
  }, [isOpen]);
  return (
    <AlertDialog isOpen={isOpen} isCentered={true} size={'5xl'}>
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            <div className="flex items-center justify-between">
              <span className="text-3xl uppercase">{t('modal_title')}</span>
              <Tooltip
                hasArrow
                label={canEdit ? t('lock_tooltip') || 'Lock' : t('edit_tooltip') || 'Edit'}
                bg="red.200"
                color="black"
                placement="bottom"
              >
                <IconButton
                  size="sm"
                  colorScheme={canEdit ? 'red' : 'blue'}
                  onClick={() => setCanEdit(!canEdit)}
                  aria-label="edit or lock"
                  icon={canEdit ? <BsBan size={18} /> : <BsPencilSquare size={18} />}
                />
              </Tooltip>
            </div>
          </AlertDialogHeader>

          <AlertDialogBody>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left Column - User Information */}
              <div className="border-r pr-4">
                <h3 className="text-xl font-bold text-blue-500 mb-4">
                  {t('user_information') || 'User Information'}
                </h3>
                <form onSubmit={handleSubmit(handleUpdateUserInfo)}>
                  <div className="my-4 grid gap-5 md:grid-cols-1">
                    {errorMassage?.message && (
                      <Error message={errorMassage?.message !== '' && errorMassage?.message} />
                    )}
                    <div>
                      <MuiSelect
                        disabled={!canEdit}
                        displayOnly={!canEdit}
                        value={roles}
                        label={t('change_user_roles')}
                        options={ROLES?.map((role) => ({
                          value: role,
                          label: role,
                        }))}
                        onChange={handleChangeValue}
                        multiple={true}
                        dataCy="select-roles"
                      />
                    </div>
                    <div>
                      <InputField
                        disabled={!canEdit}
                        displayOnly={!canEdit}
                        displayValue={user?.data?.firstName}
                        register={register}
                        variant="auth"
                        extra="mb-3"
                        label={t('firstname_label')}
                        required={true}
                        placeholder={t('firstname_placeholder')}
                        id="firstname"
                        name="firstname"
                        type="text"
                      />
                    </div>
                    <div>
                      <InputField
                        disabled={!canEdit}
                        displayOnly={!canEdit}
                        displayValue={user?.data?.lastName}
                        register={register}
                        variant="auth"
                        extra="mb-3"
                        label={t('lastname_label')}
                        required={true}
                        placeholder={t('lastname_placeholder')}
                        id="lastname"
                        name="lastname"
                        type="text"
                      />
                    </div>
                    <div>
                      <InputField
                        disabled={!canEdit}
                        displayOnly={!canEdit}
                        displayValue={user?.data?.phone}
                        register={register}
                        variant="auth"
                        extra="mb-3"
                        label={t('phone_label')}
                        required={true}
                        placeholder={t('phone_placeholder')}
                        id="phone"
                        name="phone"
                        type="text"
                      />
                    </div>
                    <div>
                      <InputField
                        disabled={!canEdit}
                        displayOnly={!canEdit}
                        displayValue={user?.data?.mail}
                        register={register}
                        variant="auth"
                        extra="mb-3"
                        label={t('email_label')}
                        required={true}
                        placeholder={t('email_placeholder')}
                        id="email"
                        name="email"
                        type="email"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end mt-4 gap-3">
                    {canEdit && (
                      <Button
                        type="submit"
                        variant="primary"
                        isLoading={loading}
                        size="sm"
                        width="140px"
                      >
                        {t('save_user_info') || 'Save User Info'}
                      </Button>
                    )}
                  </div>
                </form>
              </div>

              {/* Right Column - Geographic Scope */}
              {user?.data && (
                <GeoScopeManager
                  regions={regions}
                  departments={departments}
                  arrondissements={arrondissements}
                  selectedRegions={selectedRegions}
                  selectedDepartments={selectedDepartments}
                  selectedArrondissements={selectedArrondissements}
                  onRegionChange={handleRegionChange}
                  onDepartmentChange={handleDepartmentChange}
                  onArrondissementChange={handleArrondissementChange}
                  onUpdateGeoScope={handleUpdateGeoScope}
                  onRemoveAllGeoScope={handleRemoveAllGeoScope}
                  canEdit={canEdit}
                  loadingGeoUpdate={loadingGeoUpdate}
                  loadingRemoveAll={loadingRemoveAll}
                  user={user?.data}
                />
              )}
            </div>

            <div className="flex justify-start gap-3 mt-6 pt-4 border-t">
              <Button
                onClick={handleClose}
                size="sm"
                width="120px"
                variant={'danger'}
                className="btn-danger"
              >
                {t('cancel_button') || 'Close'}
              </Button>
            </div>
          </AlertDialogBody>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};

export default UpdateUserInformations;
